/**
 * SetChargingProfileForm — guided editor for the nested cs_charging_profiles
 * payload. No JSON box. Periods rendered as a small table with add/remove.
 */
import React, { useState } from "react";
import { Plus, X } from "lucide-react";
import type { RemoteCommandCatalogEntry } from "../../../../../api/maintenance";
import "../../../shared/maintenance-tables.scss";
import { useNotice } from "../../../shared/useNotice";

interface Period {
  start_period: string; // seconds-from-start, captured as string for the input
  limit: string;
  number_phases?: string;
}

interface FormState {
  connector_id: string;
  charging_profile_id: string;
  stack_level: string;
  charging_profile_purpose: string;
  charging_profile_kind: string;
  recurrency_kind: string;
  valid_from: string;
  valid_to: string;
  duration: string;
  start_schedule: string;
  charging_rate_unit: "A" | "W";
  min_charging_rate: string;
  transaction_id: string;
  periods: Period[];
}

const DEFAULT_STATE: FormState = {
  connector_id: "1",
  charging_profile_id: "1",
  stack_level: "0",
  charging_profile_purpose: "TxDefaultProfile",
  charging_profile_kind: "Absolute",
  recurrency_kind: "",
  valid_from: "",
  valid_to: "",
  duration: "",
  start_schedule: "",
  charging_rate_unit: "A",
  min_charging_rate: "",
  transaction_id: "",
  periods: [{ start_period: "0", limit: "32", number_phases: "" }],
};

const PURPOSES = ["ChargePointMaxProfile", "TxDefaultProfile", "TxProfile"];
const KINDS = ["Absolute", "Recurring", "Relative"];
const RECURRENCY = ["", "Daily", "Weekly"];

const toIsoOrUndef = (v: string): string | undefined => {
  if (!v) return undefined;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
};

interface Props {
  entry: RemoteCommandCatalogEntry;
  onSubmit: (meta: Record<string, unknown>) => Promise<void> | void;
  submitting: boolean;
}

const SetChargingProfileForm: React.FC<Props> = ({ entry, onSubmit, submitting }) => {
  const [s, setS] = useState<FormState>(DEFAULT_STATE);
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useNotice("error", null);

  const updatePeriod = (i: number, patch: Partial<Period>) =>
    setS((prev) => ({
      ...prev,
      periods: prev.periods.map((p, idx) => (idx === i ? { ...p, ...patch } : p)),
    }));

  const addPeriod = () =>
    setS((prev) => {
      // Avoid Array.prototype.at — ES2022, not transpiled by CRA's es5 target.
      const last = prev.periods.length ? prev.periods[prev.periods.length - 1] : undefined;
      return {
        ...prev,
        periods: [
          ...prev.periods,
          {
            start_period: String((Number(last?.start_period) || 0) + 3600),
            limit: last?.limit ?? "16",
            number_phases: "",
          },
        ],
      };
    });

  const removePeriod = (i: number) =>
    setS((prev) => ({ ...prev, periods: prev.periods.filter((_, idx) => idx !== i) }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!s.periods.length) {
      setError("At least one schedule period required");
      return;
    }
    for (let i = 0; i < s.periods.length; i++) {
      const p = s.periods[i];
      if (p.start_period === "" || Number.isNaN(Number(p.start_period))) {
        setError(`Period ${i + 1}: start_period must be a number`);
        return;
      }
      if (p.limit === "" || Number.isNaN(Number(p.limit))) {
        setError(`Period ${i + 1}: limit must be a number`);
        return;
      }
    }

    const charging_schedule: Record<string, unknown> = {
      charging_rate_unit: s.charging_rate_unit,
      charging_schedule_period: s.periods.map((p) => {
        const period: Record<string, unknown> = {
          start_period: Number(p.start_period),
          limit: Number(p.limit),
        };
        if (p.number_phases) period.number_phases = Number(p.number_phases);
        return period;
      }),
    };
    if (s.duration) charging_schedule.duration = Number(s.duration);
    if (s.start_schedule) charging_schedule.start_schedule = toIsoOrUndef(s.start_schedule);
    if (s.min_charging_rate) charging_schedule.min_charging_rate = Number(s.min_charging_rate);

    const cs_charging_profiles: Record<string, unknown> = {
      charging_profile_id: Number(s.charging_profile_id),
      stack_level: Number(s.stack_level),
      charging_profile_purpose: s.charging_profile_purpose,
      charging_profile_kind: s.charging_profile_kind,
      charging_schedule,
    };
    if (s.recurrency_kind) cs_charging_profiles.recurrency_kind = s.recurrency_kind;
    if (s.valid_from) cs_charging_profiles.valid_from = toIsoOrUndef(s.valid_from);
    if (s.valid_to) cs_charging_profiles.valid_to = toIsoOrUndef(s.valid_to);
    if (s.transaction_id) cs_charging_profiles.transaction_id = Number(s.transaction_id);

    const meta = {
      connector_id: Number(s.connector_id),
      cs_charging_profiles,
    };
    await onSubmit(meta);
  };

  const confirmOk = !entry.destructive || !entry.confirm_text || confirm.trim() === entry.confirm_text;

  return (
    <form className="rcmd-form" onSubmit={handleSubmit}>
      <p className="rcmd-form-description">{entry.description}</p>
      {error && <div className="rcmd-error">{error}</div>}

      <div className="rcmd-form-grid">
        <label className="rcmd-field">
          Connector<span className="rcmd-field-required">*</span>
          <input type="number" min={0} max={64} value={s.connector_id}
            onChange={(e) => setS({ ...s, connector_id: e.target.value })} required />
          <span className="rcmd-field-help">0 = whole device (only for ChargePointMaxProfile)</span>
        </label>
        <label className="rcmd-field">
          Profile ID<span className="rcmd-field-required">*</span>
          <input type="number" value={s.charging_profile_id}
            onChange={(e) => setS({ ...s, charging_profile_id: e.target.value })} required />
        </label>
        <label className="rcmd-field">
          Stack level<span className="rcmd-field-required">*</span>
          <input type="number" min={0} value={s.stack_level}
            onChange={(e) => setS({ ...s, stack_level: e.target.value })} required />
        </label>
        <label className="rcmd-field">
          Purpose<span className="rcmd-field-required">*</span>
          <select value={s.charging_profile_purpose}
            onChange={(e) => setS({ ...s, charging_profile_purpose: e.target.value })}>
            {PURPOSES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
        <label className="rcmd-field">
          Kind<span className="rcmd-field-required">*</span>
          <select value={s.charging_profile_kind}
            onChange={(e) => setS({ ...s, charging_profile_kind: e.target.value })}>
            {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </label>
        <label className="rcmd-field">
          Recurrency (if Recurring)
          <select value={s.recurrency_kind}
            onChange={(e) => setS({ ...s, recurrency_kind: e.target.value })}>
            {RECURRENCY.map((r) => <option key={r} value={r}>{r || "— none —"}</option>)}
          </select>
        </label>
        <label className="rcmd-field">
          Valid from
          <input type="datetime-local" value={s.valid_from}
            onChange={(e) => setS({ ...s, valid_from: e.target.value })} />
        </label>
        <label className="rcmd-field">
          Valid to
          <input type="datetime-local" value={s.valid_to}
            onChange={(e) => setS({ ...s, valid_to: e.target.value })} />
        </label>
        <label className="rcmd-field">
          Transaction ID (TxProfile only)
          <input type="number" value={s.transaction_id}
            onChange={(e) => setS({ ...s, transaction_id: e.target.value })} />
        </label>
        <label className="rcmd-field">
          Rate unit<span className="rcmd-field-required">*</span>
          <select value={s.charging_rate_unit}
            onChange={(e) => setS({ ...s, charging_rate_unit: e.target.value as "A" | "W" })}>
            <option value="A">Amps</option>
            <option value="W">Watts</option>
          </select>
        </label>
        <label className="rcmd-field">
          Schedule duration (s)
          <input type="number" min={0} value={s.duration}
            onChange={(e) => setS({ ...s, duration: e.target.value })} />
        </label>
        <label className="rcmd-field">
          Start schedule at
          <input type="datetime-local" value={s.start_schedule}
            onChange={(e) => setS({ ...s, start_schedule: e.target.value })} />
        </label>
        <label className="rcmd-field">
          Min charging rate
          <input type="number" min={0} value={s.min_charging_rate}
            onChange={(e) => setS({ ...s, min_charging_rate: e.target.value })} />
        </label>
      </div>

      <div className="rcmd-section-label" style={{ marginTop: 16 }}>
        Schedule periods<span className="rcmd-field-required">*</span>
      </div>
      <div className="rcmd-periods-editor">
        <div className="rcmd-table-editor">
          <table className="maint-stack">
            <thead>
              <tr>
                <th>#</th>
                <th>Start offset (s)</th>
                <th>Limit ({s.charging_rate_unit})</th>
                <th>Phases</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {s.periods.map((p, i) => (
                <tr key={i}>
                  <td data-label="#" style={{ width: 32, color: "#999" }}>{i + 1}</td>
                  <td data-label="Start offset (s)"><input type="number" min={0} value={p.start_period}
                    onChange={(e) => updatePeriod(i, { start_period: e.target.value })} /></td>
                  <td data-label={`Limit (${s.charging_rate_unit})`}><input type="number" value={p.limit}
                    onChange={(e) => updatePeriod(i, { limit: e.target.value })} /></td>
                  <td data-label="Phases"><input type="number" min={1} max={3} placeholder="1–3"
                    value={p.number_phases ?? ""}
                    onChange={(e) => updatePeriod(i, { number_phases: e.target.value })} /></td>
                  <td className="rcmd-cell-actions">
                    {s.periods.length > 1 && (
                      <button type="button" className="rcmd-table-row-remove" onClick={() => removePeriod(i)}>
                        <X size={12} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="rcmd-table-footer">
            <span>{s.periods.length} period{s.periods.length === 1 ? "" : "s"}</span>
            <button type="button" className="maint-btn-small" onClick={addPeriod}>
              <Plus size={12} /> Add period
            </button>
          </div>
        </div>
      </div>

      <div className="rcmd-form-actions">
        {entry.destructive && entry.confirm_text && (
          <input className="rcmd-confirm" value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder={`Type "${entry.confirm_text}" to confirm`} />
        )}
        <button type="submit" className="maint-btn-primary" disabled={submitting || !confirmOk}>
          {submitting ? "Sending…" : "Send"}
        </button>
      </div>
    </form>
  );
};

export default SetChargingProfileForm;
